import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2, Search } from "lucide-react";

const EMPTY = { nome: "", partita_iva: "", codice_fiscale: "", indirizzo: "", citta: "", cap: "", telefono: "", email: "", pec: "", codice_destinatario: "", codice_ateco: "", note: "" };

export default function Companies() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [query, setQuery] = useState("");

  const load = () => api.get("/companies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter((c) =>
        [c.nome, c.partita_iva, c.codice_fiscale, c.indirizzo]
          .some((v) => (v || "").toLowerCase().includes(q)))
    : items;

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Il nome è obbligatorio");
    try {
      const payload = { nome: form.nome, partita_iva: form.partita_iva, codice_fiscale: form.codice_fiscale, indirizzo: form.indirizzo, citta: form.citta, cap: form.cap, telefono: form.telefono, email: form.email, pec: form.pec, codice_destinatario: form.codice_destinatario, codice_ateco: form.codice_ateco, note: form.note };
      if (editId) await api.put(`/companies/${editId}`, payload);
      else await api.post("/companies", payload);
      toast.success(editId ? "Azienda aggiornata" : "Azienda creata");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa azienda?")) return;
    await api.delete(`/companies/${id}`);
    toast.success("Azienda eliminata"); load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Aziende Clienti</h1>
          <p className="text-sm text-slate-500 mt-1">Anagrafica delle società collegate a servizi e vendite.</p>
        </div>
        <Button onClick={openNew} data-testid="add-company-btn" className="bg-dusty-600 hover:bg-dusty-700 text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Nuova Azienda
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca per nome, P.IVA, C.F. o indirizzo…"
          data-testid="company-search"
          className="pl-9 bg-white"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Card key={c.id} className="p-5 border-slate-200 shadow-sm" data-testid={`company-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-dusty-50 text-dusty-600 flex items-center justify-center">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold text-slate-800">{c.nome}</div>
                  {c.partita_iva && <div className="text-xs text-slate-500">P.IVA {c.partita_iva}</div>}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(c.id)} data-testid={`delete-company-${c.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            </div>
            {c.indirizzo && <div className="text-sm text-slate-500 mt-3">{c.indirizzo}</div>}
            {c.note && <div className="text-sm text-slate-400 mt-1">{c.note}</div>}
          </Card>
        ))}
        {filtered.length === 0 && <div className="text-slate-400 col-span-full py-12 text-center">{q ? "Nessuna azienda trovata." : "Nessuna azienda. Aggiungine una."}</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="company-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica Azienda" : "Nuova Azienda"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome *</Label><Input value={form.nome} onChange={set("nome")} data-testid="company-nome" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={set("partita_iva")} data-testid="company-piva" className="mt-1.5" /></div>
              <div><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={set("codice_fiscale")} className="mt-1.5" /></div>
            </div>
            <div><Label>Indirizzo (Via/n°)</Label><Input value={form.indirizzo} onChange={set("indirizzo")} data-testid="company-indirizzo" className="mt-1.5" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Città</Label><Input value={form.citta} onChange={set("citta")} data-testid="company-citta" className="mt-1.5" /></div>
              <div><Label>CAP</Label><Input value={form.cap} onChange={set("cap")} data-testid="company-cap" className="mt-1.5" /></div>
              <div><Label>Telefono</Label><Input value={form.telefono} onChange={set("telefono")} data-testid="company-telefono" className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input value={form.email} onChange={set("email")} data-testid="company-email" className="mt-1.5" /></div>
              <div><Label>PEC</Label><Input value={form.pec} onChange={set("pec")} data-testid="company-pec" className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Codice destinatario (SDI)</Label><Input value={form.codice_destinatario} onChange={set("codice_destinatario")} data-testid="company-sdi" className="mt-1.5" /></div>
              <div><Label>Codice ATECO</Label><Input value={form.codice_ateco} onChange={set("codice_ateco")} data-testid="company-ateco" className="mt-1.5" /></div>
            </div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={set("note")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="company-save" className="bg-dusty-600 hover:bg-dusty-700 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
